import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { normalizePatientEmailHistoryStatus, type PatientEmailStatus } from '@/lib/emailHistory';

export interface PatientEmailHistoryItem {
  id: string;
  clinic_id: string;
  patient_id: string;
  sent_to: string;
  sent_by: string | null;
  sender: string | null;
  subject: string;
  email_type: string;
  status: PatientEmailStatus;
  error_message: string | null;
  provider_message_id: string | null;
  created_at: string;
  invoice_id: string | null;
  invoice_number: string | null;
  appointment_id: string | null;
  quote_id: string | null;
  questionnaire_assignment_id: string | null;
  invoice_reminder_id: string | null;
}

export const patientEmailHistoryQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['email-history', 'patient', clinicId, patientId] as const;

export function usePatientEmailHistory(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: patientEmailHistoryQueryKey(clinicId, patientId),
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<PatientEmailHistoryItem[]> => {
      if (!clinicId || !patientId) return [];

      const { data, error } = await supabase.rpc('get_patient_email_history', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });

      if (error) throw error;

      return (data ?? []).map((row): PatientEmailHistoryItem => ({
        id: row.id,
        clinic_id: row.clinic_id,
        patient_id: row.patient_id,
        sent_to: row.sent_to,
        sent_by: row.sent_by,
        sender: row.sender ?? null,
        subject: row.subject,
        email_type: row.email_type,
        status: normalizePatientEmailHistoryStatus(row.status),
        error_message: row.error_message,
        provider_message_id: row.provider_message_id,
        created_at: row.created_at,
        invoice_id: row.invoice_id ?? null,
        invoice_number: row.invoice_number ?? null,
        appointment_id: row.appointment_id ?? null,
        quote_id: row.quote_id ?? null,
        questionnaire_assignment_id: row.questionnaire_assignment_id ?? null,
        invoice_reminder_id: row.invoice_reminder_id ?? null,
      }));
    },
    staleTime: 15_000,
  });
}
